/**
 * User and Dashboard API
 */

import { apiClient, ApiResponse, ApiError, ApiErrorResponse } from './client';

export interface MentorAssignment {
  cohortId: string;
  role: string;
  grantedAt?: string;
}

export interface ParticipantEnrollment {
  cohortId: string;
  role: string;
  status: string;
  enrolledAt?: string;
}

export interface ProgramAdminRole {
  programId: string;
  role: string;
  grantedAt?: string;
}

export interface UserPermissions {
  userId: string;
  isManager?: boolean;
  programsAdmin: ProgramAdminRole[];
  cohortsMentor: MentorAssignment[];
  cohortsEnrolled: ParticipantEnrollment[];
}

export interface UserRequirement {
  [key: string]: unknown;
}

export interface UserSearchResult {
  login: string;
  firstName: string;
  lastName: string;
  name: string;
  email: string;
  title: string;
}

export interface DirectReport {
  login: string;
  firstName: string;
  lastName: string;
  name: string;
  email: string;
  title: string;
}

// New Dashboard Data Types
export interface DashboardProgram {
  programId: string;
  programName: string;
  description: string;
  category: string;
  roles: ('owner' | 'admin' | 'mentor' | 'participant')[];
  hasActiveCohorts: boolean;
  cohorts: DashboardCohortDetail[];
}

export interface DashboardCohortDetail {
  cohortId: string;
  cohortName: string;
  programName: string;
  description: string;
  status: string;
  startDate: string;
  endDate: string;
  capacity: number;
  enrollmentCount: number;
  userRoles: ('owner' | 'admin' | 'mentor' | 'participant')[];
  roleData?: {
    participantProgress?: {
      completedActivities: number;
      totalActivities: number;
      percentage: number;
    };
    mentorInfo?: {
      mentorId: string;
      mentorName: string;
    };
    mentorData?: {
      menteeCount: number;
      mentees: Array<{
        userId: string;
        userName: string;
        completedActivities: number;
        totalActivities: number;
        percentage: number;
      }>;
      pendingActivityReviews: number;
    };
    adminData?: {
      enrollmentCount: number;
      capacity: number;
      pendingMentorRequests: number;
      pendingGraduations: number;
      unassignedParticipants: number;
    };
  };
}

export interface DashboardData {
  userId: string;
  programs: DashboardProgram[];
}

/**
 * User API Service
 */
export const usersApi = {
  /**
   * Search users via PAPI
   */
  async searchUsers(query: string, managersOnly = false): Promise<UserSearchResult[]> {
    const params = new URLSearchParams({ q: query });
    if (managersOnly) params.set('managersOnly', 'true');
    const response = await apiClient.get<ApiResponse<UserSearchResult[]>>(
      `/users/search?${params.toString()}`
    );
    if (!response.success) throw new ApiError(response as unknown as ApiErrorResponse);
    return response.data;
  },

  /**
   * Get direct reports for a manager via PAPI
   */
  async getDirectReports(userId: string): Promise<DirectReport[]> {
    const response = await apiClient.get<ApiResponse<{ directReports: DirectReport[] }>>(
      `/users/${userId}/direct-reports`
    );
    if (!response.success) throw new ApiError(response as unknown as ApiErrorResponse);
    return response.data.directReports;
  },

  /**
   * Get user dashboard data (all programs, cohorts, and roles)
   */
  async getDashboardData(userId: string): Promise<DashboardData> {
    const response = await apiClient.get<ApiResponse<DashboardData>>(
      `/users/${userId}/dashboard`
    );
    if (!response.success) throw new ApiError(response as unknown as ApiErrorResponse); return response.data;
  },

  /**
   * Get user permissions across all programs (admin roles, mentor cohorts, enrollments).
   * Called once on app load to populate role-checking state in the auth store.
   */
  async getPermissions(userId: string): Promise<UserPermissions> {
    const response = await apiClient.get<ApiResponse<UserPermissions>>(
      `/users/${userId}/permissions`
    );
    if (!response.success) throw new ApiError(response as unknown as ApiErrorResponse); return response.data;
  },
};

/**
 * Dashboard API Service
 */
export const dashboardApi = {
  // Leadership dashboard removed - not used in POC
};
